import type { ComponentDocument, RenderRead, TableRequest } from "./types";
import fpFetch from "@/services/fpFetch";
import { BASE_API_URL_V2 } from "@/constants";
import { parseInstant, type Temporal } from "@/utils/temporal";
import { clearProductTimeout, setProductTimeout } from "@/utils/productTimers";
import type {
  BuilderDraft,
  BuilderOpenedFrom,
  BuilderState,
  PageSelector,
  FormFactor,
} from "./builderTypes";
export type NativePanelKind =
  | "latest_read"
  | "recent_interactions"
  | "recent_documents"
  | "key_facts"
  | "relationships";
/** What a card renders, recorded when its revision was approved. */
export type ComponentRootKind = "chart" | "table" | "metric" | "other";
export type ComponentSummary = BuilderDraft["metadata"] & {
  native_panel?: NativePanelKind | null;
  /** Null (or absent) on a revision approved before kinds were recorded. */
  root_kind?: ComponentRootKind | null;
  id: string;
  current_revision_id: string;
  revision: number;
  permission: "view" | "edit" | "admin";
  archived: boolean;
  can_archive?: boolean;
  created_by: string;
  /** The component has an organization grant, so an organization layout may place it. */
  organization_shared: boolean;
  updated_at: Extract<Temporal, { granularity: "instant" }>;
  /** A copy's original, by name, when the viewer can see it (#13714 L1.5). */
  variant_of: string | null;
};
export interface ComponentPage {
  components: ComponentSummary[];
  next_after: string | null;
}
export type CreateComponentBuilder = {
  context: PageSelector;
  /** Absent means a page; only a new widget can come from the library (#14521). */
  opened_from?: BuilderOpenedFrom;
} & (
  | { component_id?: never; intent?: never }
  | { component_id: string; intent: "edit" | "copy" }
);
const base = `${BASE_API_URL_V2}/api/v2/rms/components`;
/**
 * What the surface calls a component: a widget on the dashboards, the library and the
 * builder; a card on a record page (#13123).
 */
export type ComponentNoun = "widget" | "card";
/** The generic failure a component shows when it cannot say more. */
export const failedToLoad = (noun: ComponentNoun): string =>
  `This ${noun} couldn't load. Please retry.`;
/** A component the server refused: gone, or no longer the viewer's to see. */
export const unavailableOrAccessChanged = (noun: ComponentNoun): string =>
  `This ${noun} is unavailable or your access changed.`;
/** A component that changed since the surface read it. */
export const changedReload = (noun: ComponentNoun): string =>
  `This ${noun} changed. Reload and try again.`;
/** A component whose render the server never answered. */
export const didNotAnswer = (noun: ComponentNoun): string =>
  `This ${noun} didn't answer. Please retry.`;
/** A failure raised for a component, worded with the noun of the surface it was raised for. */
export class ComponentError extends Error {
  readonly noun: ComponentNoun;
  constructor(noun: ComponentNoun, message: string) {
    super(message);
    this.noun = noun;
  }
}
/** A components request the server answered with an error, keeping its status. */
export class ComponentRequestError extends ComponentError {
  readonly status: number;
  constructor(status: number, noun: ComponentNoun) {
    super(
      noun,
      status === 403 || status === 404
        ? unavailableOrAccessChanged(noun)
        : status === 409
          ? changedReload(noun)
          : failedToLoad(noun)
    );
    this.name = "ComponentRequestError";
    this.status = status;
  }
}
/**
 * The noun of every surface these requests serve: the library, the builder and the dashboards
 * read them, and the record-page layout editor reads its own (`pageLayoutsApi`).
 */
const REQUEST_NOUN: ComponentNoun = "widget";
/** Whether a components request was refused (the gate, or access lost), not a passing fault. */
export const isComponentRefusal = (error: unknown): boolean =>
  error instanceof ComponentRequestError &&
  (error.status === 403 || error.status === 404);
async function request<T>(
  suffix: string,
  signal: AbortSignal,
  body?: object
): Promise<T> {
  const response = await fpFetch(`${base}${suffix}`, {
    method: body ? "POST" : "GET",
    signal,
    headers: { "Content-Type": "application/json" },
    ...(body ? { body: JSON.stringify(body) } : {}),
  });
  if (!response.ok)
    throw new ComponentRequestError(response.status, REQUEST_NOUN);
  return response.json();
}
/** A page of the catalogue a reader scrolls through. */
export const COMPONENT_PAGE_SIZE = 50;
/**
 * The most components the server lists in one page: a surface that reads the whole catalogue
 * before it can start asks for this many, so a catalogue under it loads in one request.
 */
export const COMPONENT_PAGE_MAX = 200;
export const listComponents = (
  after: string | null,
  includeArchived: boolean,
  signal: AbortSignal,
  { limit, formFactor }: { limit: number; formFactor?: FormFactor }
) => {
  const params = new URLSearchParams({
    limit: String(limit),
    include_archived: String(includeArchived),
  });
  if (after) params.set("after", after);
  if (formFactor) params.set("form_factor", formFactor);
  return request<ComponentPage>(`?${params}`, signal);
};
/** A record previews start from: the organization's busiest of the given kinds, or null. */
export interface PreviewRecord {
  entity_id: string;
  name: string;
  kind: string;
}
export const getPreviewRecord = (
  kinds: readonly string[],
  signal: AbortSignal
) => {
  const params = new URLSearchParams(kinds.map((kind) => ["kinds", kind]));
  return request<PreviewRecord | null>(`/preview-record?${params}`, signal);
};
export const getComponentSummary = (id: string, signal: AbortSignal) =>
  request<ComponentSummary>(`/${encodeURIComponent(id)}`, signal);
/** An approved revision's version, approver and approval time (the Update card captions). */
export interface RevisionApproval {
  id: string;
  revision: number;
  /** Null for a revision the platform seeded, or an approver since removed. */
  approved_by_name: string | null;
  approved_at: Extract<Temporal, { granularity: "instant" }>;
}
export const getRevisionApproval = (
  componentId: string,
  revisionId: string,
  signal: AbortSignal
) =>
  request<RevisionApproval>(
    `/${encodeURIComponent(componentId)}/revisions/${encodeURIComponent(revisionId)}`,
    signal
  );
export const createComponentBuilder = (
  body: CreateComponentBuilder,
  signal: AbortSignal
) => request<BuilderState>("/builders", signal, body);
export const archiveComponent = (id: string, signal: AbortSignal) =>
  request<ComponentSummary>(`/${encodeURIComponent(id)}/archive`, signal, {});

/** Where a widget's appendix is being written from: the dashboard tab and widget, or none. */
export interface AppendixPlace {
  view_id: string;
  widget_id: string;
  tab_id?: string;
}
/** A run that writes a widget's appendix (deep-dive-depth, requirement 1). */
export interface AppendixRun {
  component_id: string;
  status: "running" | "written" | "unchanged" | "refused" | "failed";
  /** Why a run that ended wrote nothing, as the builder or its checks said. */
  reason: string | null;
  revision_id: string | null;
  /** A written run's render, appendix and all, as its proposal previewed it. */
  document: ComponentDocument | null;
}
/**
 * Start writing a widget's appendix: a builder revision that may only explain it. A start the
 * server refuses (the run already going, a widget not on the dashboard) throws with its reason.
 */
export async function writeAppendix(
  componentId: string,
  body: Partial<AppendixPlace> & { viewer_timezone?: string }
): Promise<AppendixRun> {
  const response = await fpFetch(
    `${base}/${encodeURIComponent(componentId)}/appendix`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    }
  );
  if (response.ok) return response.json();
  const answer: unknown = await response.json().catch(() => null);
  const detail =
    typeof answer === "object" &&
    answer !== null &&
    "detail" in answer &&
    typeof answer.detail === "string"
      ? answer.detail
      : null;
  throw detail
    ? new Error(detail)
    : new ComponentRequestError(response.status, REQUEST_NOUN);
}
/** The widget's latest appendix run, or null when none was ever started. */
export const getAppendixRun = (componentId: string, signal: AbortSignal) =>
  request<{ run: AppendixRun | null }>(
    `/${encodeURIComponent(componentId)}/appendix`,
    signal
  ).then((answer) => answer.run);

/** The longest name a widget takes (the server's `COMPONENT_NAME_MAX`). */
export const COMPONENT_NAME_MAX = 120;

/**
 * Renames a library widget everywhere it is named, without a new revision (its editors only):
 * the summary with its new name.
 */
export async function renameComponent(
  id: string,
  name: string
): Promise<ComponentSummary> {
  const response = await fpFetch(`${base}/${encodeURIComponent(id)}`, {
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ name }),
  });
  if (!response.ok)
    throw new ComponentRequestError(response.status, REQUEST_NOUN);
  return response.json();
}

/** One placement of a page render batch: the component as placed, with its table requests. */
export interface BatchPlacement {
  component_id: string;
  revision_id: string;
  tables: Readonly<Record<string, TableRequest>>;
}
/** One streamed answer of a page render batch, for the placement at `index`. */
export type BatchLine =
  | {
      index: number;
      component_id: string;
      status: "ok";
      document: ComponentDocument;
      /**
       * When the server rendered this answer (epoch ms), for one it answered from its render
       * cache (`cached_at`); null for a render made for this request.
       */
      cachedAt: number | null;
      /**
       * The kinds the render read (#13380: "manager", "interaction", "document", ...), as the
       * line names them; null when the line names none (a server from before the field).
       */
      kindsRead?: string[] | null;
      /**
       * Whether this render is the one that first recorded its revision's kind (#13380): true
       * once per revision, false on a cached line and on every later render.
       */
      kindRecorded?: boolean;
      /**
       * The server answered from a cache entry past its fresh window (dashboard polish,
       * requirement 15): shown at once while the page renders the placement again past it.
       */
      stale?: boolean;
    }
  | {
      index: number;
      component_id: string;
      status: "error";
      code: string;
      message: string;
    };
/** The server's bound on placements per batch request. */
export const MAX_BATCH_PLACEMENTS = 12;
/**
 * How many placements one server process renders at once (`DEFAULT_ADMISSION` in
 * `fp/rms/components/runtime.py`), which is also how many prepared brokers each worker keeps
 * warm (`prepared_brokers.POOL_SIZE`). Every surface that renders cards in batches sends
 * batches of this size: the pages (Home, a record's Overview, a view's summary), their
 * prefetch, the library and the layout editor. Each batch is one round of renders on warm
 * brokers, so no placement waits for a turn or a cold broker inside its own request; on stage a
 * fifth card in one request took a cold broker, 200–590 ms to acquire and 1.2–1.5 s to run,
 * against about 0.1–0.5 s warm. A seven-card page therefore goes out as four and three, which
 * `MAX_REQUESTS_IN_FLIGHT` lets run side by side on the two sync workers, each with its own
 * warm pool.
 */
export const RENDER_ROUND_PLACEMENTS = 4;

/**
 * The server turned the batch away (429: the user's render jobs are at their cap; 503: the
 * render job store is unreachable, #12902): nothing was started, and the batch may be sent
 * again after `retryAfterMs`.
 */
export class RenderBusyError extends Error {
  constructor(readonly retryAfterMs: number) {
    super("The server is busy rendering cards.");
    this.name = "RenderBusyError";
  }
}

/** A `Retry-After` header in milliseconds: delay-seconds only; one second when absent. */
const retryAfterMs = (header: string | null): number => {
  const seconds = header === null ? NaN : Number(header);
  return Number.isFinite(seconds) && seconds >= 0 ? seconds * 1000 : 1000;
};

/**
 * How a batch may use the server's render cache (rms-dashboards, Rendering and speed): `cache`
 * answers a placement from the viewer's own recent render of it; `fresh` (Refresh) renders it
 * again and replaces that entry. Absent, every answer is a live render.
 */
export interface RenderBatchFlags {
  cache?: boolean;
  fresh?: boolean;
}

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" && value !== null && !Array.isArray(value);

/** A component document's envelope; its nodes are checked by the renderer, as a single
 * preview's are, so a malformed node fails only its own card. */
const isComponentDocument = (value: unknown): value is ComponentDocument =>
  isRecord(value) &&
  value.version === 1 &&
  isRecord(value.root) &&
  value.root.tag === "component" &&
  Array.isArray(value.root.children);

/**
 * A cache hit's `cached_at`, the instant wire shape, in epoch ms; null when absent; undefined
 * for any other shape, a broken contract that fails its own card rather than passing for a
 * live render.
 */
const cachedAtOf = (value: unknown): number | null | undefined => {
  if (value === undefined || value === null) return null;
  if (
    isRecord(value) &&
    value.granularity === "instant" &&
    typeof value.at === "string"
  ) {
    const instant = parseInstant(value.at);
    if (instant) return instant.epochMilliseconds;
  }
  return undefined;
};

/** One of a line's `reads`, when it has the contract's shape; anything else is skipped. */
const renderReadOf = (value: unknown): RenderRead | null => {
  if (
    !isRecord(value) ||
    typeof value.kind !== "string" ||
    typeof value.from !== "string" ||
    typeof value.count !== "number" ||
    !Array.isArray(value.filters)
  )
    return null;
  return {
    kind: value.kind,
    from: value.from,
    filters: value.filters.filter(
      (filter): filter is string => typeof filter === "string"
    ),
    count: value.count,
    as_of: typeof value.as_of === "string" ? value.as_of : null,
  };
};

/**
 * The document a line carries, with what its render read (dashboard-widget-deep-dive,
 * requirement 3) kept on it, so the reads go wherever the document is kept. A line without
 * `reads` (a server from before the field) leaves the document as it came.
 */
const withReads = (
  document: ComponentDocument,
  reads: unknown,
  more: unknown
): ComponentDocument =>
  Array.isArray(reads)
    ? {
        ...document,
        reads: {
          reads: reads.flatMap((read) => {
            const parsed = renderReadOf(read);
            return parsed ? [parsed] : [];
          }),
          more: typeof more === "number" && more > 0 ? more : 0,
        },
      }
    : document;

function parseBatchLine(line: unknown, noun: ComponentNoun): BatchLine {
  if (
    isRecord(line) &&
    typeof line.index === "number" &&
    typeof line.component_id === "string"
  ) {
    const { index, component_id } = line;
    if (line.status === "ok" && isComponentDocument(line.document)) {
      const cachedAt = cachedAtOf(line.cached_at);
      return cachedAt === undefined
        ? {
            index,
            component_id,
            status: "error",
            code: "invalid",
            // The client's own line for a document it could not read: no reason from
            // the server, so the card shows only its generic text (#12951).
            message: "",
          }
        : {
            index,
            component_id,
            status: "ok",
            document: withReads(line.document, line.reads, line.reads_more),
            cachedAt,
            kindsRead: Array.isArray(line.kinds_read)
              ? line.kinds_read.filter(
                  (kind): kind is string => typeof kind === "string"
                )
              : null,
            kindRecorded: line.kind_recorded === true,
            ...(line.stale === true ? { stale: true } : {}),
          };
    }
    if (
      line.status === "error" &&
      typeof line.code === "string" &&
      typeof line.message === "string"
    )
      return {
        index,
        component_id,
        status: "error",
        code: line.code,
        message: line.message,
      };
  }
  throw new ComponentError(noun, failedToLoad(noun));
}

/** The first read of a batch's render job, this long after its POST was sent (#12902). */
export const RENDER_POLL_FIRST_MS = 500;
/** Each wait between reads of a render job is this many times the last one... */
export const RENDER_POLL_GROWTH = 1.5;
/** ...up to this long. */
export const RENDER_POLL_MAX_MS = 2000;
/**
 * How long a batch waits for its render job, from the POST's answer, before giving up on it:
 * its cards without a line then fail as not answered, and offer Retry. Long enough for a job
 * that only waited in the queue under a burst (a cold run painted at 58 s).
 */
export const RENDER_JOB_WAIT_MS = 120_000;

/**
 * Tells a batch waiting on its render job that the job has moved (the user room's
 * `RMS_RENDER_BATCH_PROGRESS` frame), so it reads the job now rather than at its next poll.
 * A page creates one and hands it to its batcher (`createRenderPushSource`).
 */
export interface RenderPushSource {
  /** Calls `wake` on each push for `batchId` until the returned function is called. */
  subscribe(batchId: string, wake: () => void): () => void;
}

/** A batch's cache flags and, when the page listens to its user room, its push source. */
export interface RenderBatchOptions extends RenderBatchFlags {
  push?: RenderPushSource;
  /** What the surface calls the components its failures name; a card when absent. */
  noun?: ComponentNoun;
}

const renderAbortError = () =>
  new DOMException("Component render was canceled", "AbortError");

/** The POST's answer: the lines it settled itself, and the indices its render job answers. */
interface AcceptedBatch {
  batchId: string;
  lines: unknown[];
  pending: number[];
}

/** One read of a render job: every line it has produced so far, and whether it is done. */
interface RenderJobRead {
  done: boolean;
  lines: unknown[];
}

const parseAccepted = (body: unknown, noun: ComponentNoun): AcceptedBatch => {
  if (
    isRecord(body) &&
    typeof body.batch_id === "string" &&
    Array.isArray(body.lines) &&
    Array.isArray(body.pending) &&
    body.pending.every((index) => typeof index === "number")
  )
    return {
      batchId: body.batch_id,
      lines: body.lines,
      pending: body.pending as number[],
    };
  throw new ComponentError(noun, failedToLoad(noun));
};

const parseJobRead = (body: unknown, noun: ComponentNoun): RenderJobRead => {
  if (
    isRecord(body) &&
    typeof body.done === "boolean" &&
    Array.isArray(body.lines)
  )
    return { done: body.done, lines: body.lines };
  throw new ComponentError(noun, failedToLoad(noun));
};

/**
 * Renders a page's placements as one batch (#11849 render latency; #12902 async renders).
 * `onLine` hears each placement's line once, as it arrives, and the promise settles when no
 * further line will come; a placement left without one has not answered. Every answer is
 * authorized for its own placement; only a batch sent with `cache` may be answered from the
 * server's render cache.
 *
 * The POST answers at once (202) with what the server settled itself (cache hits, refusals)
 * and the placements its render job will answer. Those are read from the job (GET) until it
 * is done: the first read `RENDER_POLL_FIRST_MS` after the POST was sent (at once on its
 * answer, when that came later), each wait `RENDER_POLL_GROWTH` times the last up to
 * `RENDER_POLL_MAX_MS`, and a push for the batch ending a wait at once. A 404 (the job is
 * gone) or `RENDER_JOB_WAIT_MS` without the job done ends the batch. Aborting `signal` stops
 * the reads and cancels the job (DELETE), so no render worker keeps rendering for a batch
 * nobody will read.
 */
export async function streamRenderBatch(
  context: PageSelector,
  placements: readonly BatchPlacement[],
  signal: AbortSignal,
  onLine: (line: BatchLine) => void,
  { push, cache, fresh, noun = "card" }: RenderBatchOptions = {}
): Promise<void> {
  // The first read's wait starts with the request, so a slow answer is read from at once.
  const firstWait = wakeable(RENDER_POLL_FIRST_MS);
  try {
    const response = await fpFetch(`${base}/render-batch`, {
      method: "POST",
      signal,
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        context,
        placements,
        ...(cache ? { cache: true } : {}),
        ...(fresh ? { fresh: true } : {}),
      }),
    });
    if (response.status === 429 || response.status === 503)
      throw new RenderBusyError(
        retryAfterMs(response.headers.get("Retry-After"))
      );
    // The batch refused as a whole (the viewer signed out or lost access to the page) is a
    // refusal, which a card shows whatever answer it holds (#15213); anything else is a
    // failed render.
    if (response.status === 401 || response.status === 403)
      throw new ComponentRequestError(response.status, noun);
    if (!response.ok) throw new ComponentError(noun, failedToLoad(noun));
    const accepted = parseAccepted(await response.json(), noun);
    // Each placement is answered once: a read repeats every line the job has produced.
    const answered = new Set<number>();
    const hand = (raw: unknown) => {
      const line = parseBatchLine(raw, noun);
      if (answered.has(line.index)) return;
      answered.add(line.index);
      onLine(line);
    };
    accepted.lines.forEach(hand);
    if (!accepted.pending.length) return;
    await readRenderJob(accepted.batchId, signal, hand, push, firstWait, noun);
  } finally {
    firstWait.cancel();
  }
}

/** A wait of `ms` on the product clock that can be ended early, or dropped. */
interface Wakeable {
  promise: Promise<void>;
  /** Ends the wait now. */
  wake(): void;
  /** Drops the timer; the wait never ends on its own. */
  cancel(): void;
}

const wakeable = (ms: number): Wakeable => {
  const ended = Promise.withResolvers<void>();
  const timer = setProductTimeout(ended.resolve, ms);
  return {
    promise: ended.promise,
    wake: () => {
      clearProductTimeout(timer);
      ended.resolve();
    },
    cancel: () => clearProductTimeout(timer),
  };
};

/**
 * Reads a batch's render job until it is done, gone, past its wait or abandoned. `firstWait`
 * is the wait the POST started; each later one is armed after a read.
 */
async function readRenderJob(
  batchId: string,
  signal: AbortSignal,
  hand: (raw: unknown) => void,
  push: RenderPushSource | undefined,
  firstWait: Wakeable,
  noun: ComponentNoun
): Promise<void> {
  const url = `${base}/render-batch/${encodeURIComponent(batchId)}`;
  // The wait in progress; null while a read is in flight.
  let waiting: Wakeable | null = firstWait;
  // A push that came while a read was in flight: the next wait is skipped, not slept through.
  let nudged = false;
  let expired = false;
  // Stops a read in flight, at the deadline or on the asker's abort.
  const reading = new AbortController();
  const stop = () => {
    reading.abort();
    waiting?.wake();
  };
  signal.addEventListener("abort", stop, { once: true });
  const deadline = setProductTimeout(() => {
    expired = true;
    stop();
  }, RENDER_JOB_WAIT_MS);
  const unsubscribe = push?.subscribe(batchId, () => {
    if (waiting) waiting.wake();
    else nudged = true;
  });
  let pause = RENDER_POLL_FIRST_MS;
  try {
    for (;;) {
      if (waiting) await waiting.promise;
      waiting = null;
      if (signal.aborted || expired) break;
      let read: RenderJobRead;
      try {
        const response = await fpFetch(url, {
          method: "GET",
          signal: reading.signal,
        });
        if (response.status === 404) return;
        if (!response.ok) throw new ComponentError(noun, failedToLoad(noun));
        read = parseJobRead(await response.json(), noun);
      } catch (error: unknown) {
        if (signal.aborted || expired) break;
        throw error;
      }
      read.lines.forEach(hand);
      if (read.done) return;
      pause = Math.min(pause * RENDER_POLL_GROWTH, RENDER_POLL_MAX_MS);
      waiting = nudged ? null : wakeable(pause);
      nudged = false;
    }
    if (signal.aborted) throw renderAbortError();
  } finally {
    waiting?.cancel();
    clearProductTimeout(deadline);
    signal.removeEventListener("abort", stop);
    unsubscribe?.();
    // Every asker has gone: the job stops rendering for nobody. Not awaited; its answer
    // changes nothing here.
    if (signal.aborted)
      void fpFetch(url, { method: "DELETE" }).catch(() => undefined);
  }
}
